import type { StaffingPlanInput } from '../staffing/public';
import type { OrganizationNode } from './model';

export type OrganizationPlanChange = StaffingPlanInput['organizationChanges'][number];
export type OrganizationPlanNode = OrganizationNode & { pending?: 'CREATE' | 'CHANGE' | 'END' };

export function plannedOrganizationId(key: string) { return `plan:${key}`; }

export function previewOrganizationPlan(
  organizations: readonly OrganizationNode[],
  changes: readonly OrganizationPlanChange[],
  managerNames: ReadonlyMap<string, string> = new Map(),
): OrganizationPlanNode[] {
  const nodes = new Map<string, OrganizationPlanNode>(organizations.map(node => [node.id, { ...node }]));
  for (const change of changes) {
    const id = change.operation === 'CREATE' ? plannedOrganizationId(change.key) : change.organizationId;
    if (!id) continue;
    const current = nodes.get(id);
    if (!current && change.operation !== 'CREATE') continue;
    const parentId = change.parent?.planItemKey ? plannedOrganizationId(change.parent.planItemKey) : change.parent?.id;
    nodes.set(id, {
      ...(current ?? { id, name: '', subtitle: '신설 예정', manager: '미지정', count: 0, location: '미지정' }),
      ...(change.name ? { name: change.name } : {}),
      ...(change.parent ? { parentId } : {}),
      ...(change.managerId !== undefined ? { managerId: change.managerId, manager: change.managerId === null ? '미지정' : managerNames.get(change.managerId) ?? '지정 예정' } : {}),
      pending: change.operation,
    });
  }
  return [...nodes.values()];
}

/** 현재 미리보기의 관계로 루트 이동·자기 자신·하위 조직·종료 예정 조직을 제외합니다. */
export function canReparentOrganization(nodes: readonly OrganizationPlanNode[], id: string, parentId: string) {
  const byId = new Map(nodes.map(node => [node.id, node]));
  const node = byId.get(id);
  const parent = byId.get(parentId);
  if (!node?.parentId || !parent || node.pending === 'END' || parent.pending === 'END' || id === parentId || node.parentId === parentId) return false;
  const visited = new Set<string>([id]);
  let current: OrganizationPlanNode | undefined = parent;
  while (current) {
    if (visited.has(current.id)) return false;
    visited.add(current.id);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }
  return true;
}

export function changeOrganizationPlan(
  organizations: readonly OrganizationNode[],
  changes: readonly OrganizationPlanChange[],
  id: string,
  patch: { name?: string; parentId?: string; managerId?: string | null },
): OrganizationPlanChange[] {
  const previous = changes.find(change => change.operation === 'CREATE' ? plannedOrganizationId(change.key) === id : change.organizationId === id);
  const original = organizations.find(node => node.id === id);
  if (!original && previous?.operation !== 'CREATE') return [...changes];
  const next: OrganizationPlanChange = previous ? { ...previous } : { key: `organization:${id}`, operation: 'CHANGE', organizationId: id };
  if (patch.name !== undefined) {
    next.name = patch.name.trim();
    if (original?.name === next.name) delete next.name;
  }
  if (patch.parentId !== undefined) {
    next.parent = patch.parentId.startsWith('plan:') ? { planItemKey: patch.parentId.slice(5) } : { id: patch.parentId };
    if (original?.parentId === patch.parentId) delete next.parent;
  }
  if (Object.hasOwn(patch, 'managerId')) {
    next.managerId = patch.managerId;
    if (original?.managerId !== undefined && original.managerId === patch.managerId) delete next.managerId;
  }
  const meaningful = next.operation === 'CREATE' || next.name || next.parent || next.managerId !== undefined || next.operation === 'END';
  if (!meaningful) return changes.filter(change => change.key !== next.key);
  return previous ? changes.map(change => change.key === next.key ? next : change) : [...changes, next];
}

/** 부모를 먼저 이동해 최종 관계가 유효해도 중간 적용에서 순환하는 상황을 줄입니다. */
export function orderOrganizationPlan(organizations: readonly OrganizationNode[], changes: readonly OrganizationPlanChange[]) {
  const byId = new Map(previewOrganizationPlan(organizations, changes).map(node => [node.id, node]));
  const depth = (change: OrganizationPlanChange) => {
    let current = byId.get(change.operation === 'CREATE' ? plannedOrganizationId(change.key) : change.organizationId ?? '');
    const seen = new Set<string>();
    while (current?.parentId && !seen.has(current.id)) { seen.add(current.id); current = byId.get(current.parentId); }
    return seen.size;
  };
  return [...changes].sort((left, right) => {
    const rank = { CREATE: 0, CHANGE: 1, END: 2 };
    return rank[left.operation] - rank[right.operation] || (left.operation === 'CHANGE' ? depth(left) - depth(right) : 0);
  });
}

export function organizationPlanLabel(change: OrganizationPlanChange) {
  if (change.operation === 'CREATE') return '조직 신설';
  if (change.operation === 'END') return '조직 종료';
  return [change.name && '이름', change.parent && '상위 조직', change.managerId !== undefined && '리더'].filter(Boolean).join(' · ') + ' 변경';
}
